/**
 * Derives the slot-selection inputs for moving an existing appointment: whether it is a service
 * booking, the service definition, the duration to preserve, and the locked primary resource.
 */

import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useGetServiceQuery, type Appointment } from '@/api/mcp-appointments';
import { getServiceAppointmentPrimaryResourceId } from '@/utils/serviceBooking';
import { isServiceCalendarBooking } from '../calendar/calendarUtils';

/**
 * Computes reschedule slot setup for an appointment, loading its service when it is service-backed.
 * @param appointment - Appointment being rescheduled, or `null` while none is selected.
 * @param enabled - Whether the reschedule surface is active and the service may be fetched.
 * @returns Service detection, loaded service, resource/effective durations, and primary resource id.
 */
export const useRescheduleSlotSetup = (appointment: Appointment | null, enabled: boolean) => {
  const isServiceBooking = appointment ? isServiceCalendarBooking(appointment) : false;
  const serviceId = appointment?.serviceId ?? appointment?.service?.id;
  const { data: service } = useGetServiceQuery(
    serviceId ?? '',
    enabled && isServiceBooking && !!serviceId
  );

  const resourceDurationMinutes = useMemo(() => {
    if (!appointment?.startTime || !appointment?.endTime) return 0;
    const start = dayjs(appointment.startTime);
    const end = dayjs(appointment.endTime);
    return start.isValid() && end.isValid() ? end.diff(start, 'minute') : 0;
  }, [appointment]);

  const serviceDurationMinutes =
    appointment?.service?.durationMinutes ?? service?.durationMinutes ?? resourceDurationMinutes;
  const durationMinutes = isServiceBooking ? serviceDurationMinutes : resourceDurationMinutes;

  const primaryResourceId = appointment
    ? getServiceAppointmentPrimaryResourceId(appointment)
    : undefined;

  return {
    isServiceBooking,
    service,
    resourceDurationMinutes,
    durationMinutes,
    primaryResourceId
  };
};
